import React, { useMemo, useState } from 'react';
import './VisibilityReportsPage.scss';
import { useTranslation } from 'react-i18next';
import { canUseVisibilityReport } from '../../shared/helpers/addons/canUseVisibilityReport';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { startOfDay } from 'date-fns';
import axios from 'axios';
import type {
  VisibilityReportListItem,
  VisibilityReportModel,
  VisibilityReportPeriodType,
} from '@brand24-original/llm-types';
import Title from '../../shared/components/title/Title';
import Loader from '../../shared/components/common/loader/Loader';
import CustomTooltip from '../../shared/components/common/tooltip/CustomTooltip';
import { useAccount } from '../../shared/contexts/account/AccountContexts';
import { PROJECTS, REPORTS, SHARED_REPORT } from '../../shared/routes/path';
import { useGetDateRange } from '../../shared/api/hooks/monitor/useGetDateRange';
import { useGetProject } from '../../shared/api/hooks/project/useGetProject';
import { defaultProjectPeriod } from '../../shared/helpers/filters/defaultProjectPeriod';
import { EffectiveFeaturesProvider } from '../../shared/contexts/effective-features/EffectiveFeaturesContext';
import { toLocalDay } from '../../shared/helpers/filters/DateRangeHelper';
import { useGetVisibilityReports } from '../../shared/api/hooks/visibility-report/useGetVisibilityReports';
import { useGenerateVisibilityReport } from '../../shared/api/hooks/visibility-report/useGenerateVisibilityReport';
import {
  useDeleteVisibilityReport,
  useDeleteVisibilityReports,
} from '../../shared/api/hooks/visibility-report/useDeleteVisibilityReport';
import { useShareVisibilityReport } from '../../shared/api/hooks/visibility-report/useShareVisibilityReport';
import { copyTextToClipboard } from '../../shared/utils/utils';
import VisibilityReportDialog from './components/VisibilityReportDialog';
import VisibilityReportList from './components/VisibilityReportList';
import useIsMobile from '../../shared/hooks/global/UseIsMobile';
import MobilePageHeader from '../../shared/components/common/mobile-menu/MobilePageHeader';

const DEFAULT_PAGE_SIZE = 20;

const parseProjectId = (value?: string): number | null => {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const VisibilityReportsPage: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { accountResponse } = useAccount();
  const { projectId: projectIdParam } = useParams<{ projectId: string }>();
  const projectId = parseProjectId(projectIdParam);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [first, setFirst] = useState(0);

  const { data: reportsPage, isLoading } = useGetVisibilityReports(projectId, page, pageSize);
  const items = reportsPage?.items ?? [];
  // Zaznaczanie ma sens dopiero, gdy jest co zaznaczać.
  const hasReports = items.length > 0;
  const { dateRange } = useGetDateRange(projectId);
  // Ta sama reguła, co przy wejściu na Overview: projekt starszy niż miesiąc startuje na WEEKLY.
  // `getProjectById` nie zwraca `defaultPeriod` (liczą je tylko listy projektów), więc liczymy sami.
  const defaultPeriodType = defaultProjectPeriod(dateRange?.oldestDate) as VisibilityReportPeriodType;
  // Dokładnie te same granice, co pasek filtrów na V2 (`useDateRange`): dół = pierwszy dzień z danymi,
  // góra = DZIŚ. Górna granica świadomie NIE idzie za `newestDate` z API — projekt, którego ostatnia
  // odpowiedź jest sprzed kilku dni, i tak musi dać się zaraportować „do dziś".
  const pickerBounds = useMemo(() => {
    const minDate = dateRange?.oldestDate ? toLocalDay(dateRange.oldestDate) : undefined;
    return { minDate, maxDate: startOfDay(new Date()) };
  }, [dateRange?.oldestDate]);

  // Projekt bierzemy z endpointu, a nie z listy w kontekście: superadmin oglądający cudzy projekt
  // nie ma go na swojej liście, a stąd dostajemy też `ownerFeatures` / `ownerAddons`.
  const { project } = useGetProject(projectId ?? 0);

  const generate = useGenerateVisibilityReport(projectId ?? 0, {
    // Świeży raport otwieramy od razu — po to się go generuje.
    onSuccess: (reportId) => navigate(`${REPORTS}/${projectId}/${reportId}`),
  });
  const remove = useDeleteVisibilityReport(projectId ?? 0);
  const removeMany = useDeleteVisibilityReports(projectId ?? 0);
  const share = useShareVisibilityReport(projectId ?? 0);

  // Addony WŁAŚCICIELA projektu — superadmin na cudzym projekcie ma widzieć to, co widzi klient.
  // Ten sam warunek bramkuje link w Panel.tsx.
  const effectiveAddons = project?.ownerAddons ?? accountResponse?.addons ?? [];
  if (
    accountResponse &&
    !canUseVisibilityReport(effectiveAddons, accountResponse.isSuperAdmin)
  ) {
    return <Navigate to={PROJECTS} replace />;
  }

  if (!accountResponse) {
    return null;
  }

  if (projectId == null) {
    return <Navigate to={PROJECTS} replace />;
  }

  const handleGenerate = (
    from: string,
    to: string,
    models: VisibilityReportModel[],
    promptIds: number[],
    periodType: VisibilityReportPeriodType
  ) => {
    setFormError(null);
    generate.mutate(
      { from, to, models, promptIds, periodType },
      {
        onError: (err) => {
          if (axios.isAxiosError(err) && typeof err.response?.data?.error === 'string') {
            setFormError(err.response.data.error);
            return;
          }
          setFormError(t('visibilityReport.errorGeneric'));
        },
      }
    );
  };

  const handlePage = (event: { first: number; rows: number }) => {
    const newPage = Math.floor(event.first / event.rows) + 1;
    if (newPage !== page || event.rows !== pageSize) {
      setPage(newPage);
      setPageSize(event.rows);
      setFirst(event.first);
    }
  };

  const shareUrlOf = (shareToken: string): string =>
    `${window.location.origin}${SHARED_REPORT}/${shareToken}`;

  // Udostępniony raport wyłączamy po potwierdzeniu (link przestaje działać wszystkim, którzy go
  // dostali); nieudostępniony po prostu dostaje link i od razu ląduje on w schowku.
  const handleShare = (report: VisibilityReportListItem) => {
    if (report.shareToken) {
      confirmDialog({
        message: t('visibilityReport.confirmUnshare'),
        header: t('visibilityReport.shareRevoke'),
        defaultFocus: 'accept',
        accept: () => share.mutate({ reportId: report.id, enabled: false }),
      });
      return;
    }

    share.mutate(
      { reportId: report.id, enabled: true },
      {
        onSuccess: (result) => {
          if (result.shareToken) {
            void copyTextToClipboard(shareUrlOf(result.shareToken));
          }
        },
      }
    );
  };

  const confirmDelete = (reportId: number) => {
    confirmDialog({
      message: t('visibilityReport.confirmDelete'),
      header: t('confirmDeletion'),
      defaultFocus: 'accept',
      accept: () => remove.mutate(reportId),
    });
  };

  const confirmBulkDelete = () => {
    confirmDialog({
      message: t('visibilityReport.confirmDeleteMany', { count: selectedIds.length }),
      header: t('confirmDeletion'),
      defaultFocus: 'accept',
      accept: () =>
        removeMany.mutate(selectedIds, {
          onSuccess: () => setSelectedIds([]),
        }),
    });
  };

  const toggleRow = (reportId: number) =>
    setSelectedIds((prev) =>
      prev.includes(reportId) ? prev.filter((id) => id !== reportId) : [...prev, reportId]
    );

  const toggleAllOnPage = (checked: boolean) => {
    const pageIds = items.map((report) => report.id);
    setSelectedIds((prev) =>
      checked
        ? [...prev, ...pageIds.filter((id) => !prev.includes(id))]
        : prev.filter((id) => !pageIds.includes(id))
    );
  };

  const openDialog = () => {
    setFormError(null);
    setIsDialogOpen(true);
  };

  // Modele w oknie generowania mają być modelami WŁAŚCICIELA projektu — superadmin nie może wybierać
  // z listy własnego konta (ta sama zasada, co na podglądzie projektu i promptu).
  const toolbar = (
    <div className="page-box__buttons">
      {hasReports && selectionMode && selectedIds.length > 0 && (
        <div className="page-box__buttons-panel">
          <Button
            id="bulk-remove-visibility-reports"
            className="action-btn action-btn--danger"
            onClick={confirmBulkDelete}
            disabled={removeMany.isPending}
          >
            <span className="ico--trash ico--xs" />
          </Button>
          <CustomTooltip
            tooltipText="visibilityReport.bulkRemove"
            targetRef="bulk-remove-visibility-reports"
          />
        </div>
      )}
      <button type="button" className="btn btn--sm" onClick={openDialog}>
        <span className="ico--report ico--xs" aria-hidden="true" />
        {t('visibilityReport.generateBtn')}
      </button>
      {hasReports && (
        <>
          <Button
            id="select-mode-visibility-reports"
            className={`action-btn${selectionMode ? ' action-btn--active' : ''}`}
            onClick={() =>
              setSelectionMode((prev) => {
                if (prev) setSelectedIds([]);
                return !prev;
              })
            }
          >
            <span className="ico--select ico--xs" />
          </Button>
          <CustomTooltip
            tooltipText="visibilityReport.selectMode"
            targetRef="select-mode-visibility-reports"
          />
        </>
      )}
    </div>
  );

  return (
    <EffectiveFeaturesProvider
      features={project?.ownerFeatures ?? accountResponse.features ?? []}
      addons={project?.ownerAddons ?? accountResponse.addons ?? []}
    >
      <div className="visibility-report-page page-content">
        <div className="page-box">
          {/* Nagłówek przykleja się do góry przy przewijaniu — ten sam mechanizm, co na
              podglądzie projektu: `page-box__filters` daje `position: sticky`, a globalny
              obserwator z `Panel` dokłada `data-stuck` (cień) po odklejeniu. */}
          {/* Telefon: tytuł i przyciski (zaznaczanie, generowanie — same ikony) w górnym pasku; pod nim
              tylko usuwanie zaznaczonych. */}
          <MobilePageHeader
            title={<span>{t('visibilityReport.title')}</span>}
            actions={
              <>
                {hasReports && (
                  <button
                    type="button"
                    className={`mobile-header__icon-btn${selectionMode ? ' mobile-header__icon-btn--active' : ''}`}
                    onClick={() =>
                      setSelectionMode((prev) => {
                        if (prev) setSelectedIds([]);
                        return !prev;
                      })
                    }
                    aria-label={t('visibilityReport.selectMode')}
                  >
                    <span className="ico--select ico--sm" />
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn--sm mobile-header__add"
                  onClick={openDialog}
                  aria-label={t('visibilityReport.generateBtn')}
                >
                  <span className="ico--report ico--xs" />
                </button>
              </>
            }
          />
          {isMobile ? (
            hasReports &&
            selectionMode &&
            selectedIds.length > 0 && (
              <div className="visibility-report-page__mobile-bulk">
                <Button
                  className="action-btn action-btn--danger"
                  onClick={confirmBulkDelete}
                  disabled={removeMany.isPending}
                  aria-label={t('visibilityReport.bulkRemove')}
                >
                  <span className="ico--trash ico--xs" />
                </Button>
              </div>
            )
          ) : (
          <div className="page-box__filters" data-observe-sticky>
            <Title
              title={t('visibilityReport.title')}
              childrenOnMain={<div className="title-box__subtitle">{t('visibilityReport.subtitle')}</div>}
            >
              {toolbar}
            </Title>
          </div>
          )}

          <div className="page-box__stats">
            {generate.isPending && (
              <div className="visibility-report-page__generating">
                {t('visibilityReport.generatingInfo')}
              </div>
            )}

            {isLoading ? (
              <Loader />
            ) : (
              <VisibilityReportList
                projectId={projectId}
                reports={items}
                total={reportsPage?.total ?? 0}
                first={first}
                pageSize={pageSize}
                onPage={handlePage}
                onOpen={(reportId) => navigate(`${REPORTS}/${projectId}/${reportId}`)}
                onShare={handleShare}
                onDelete={confirmDelete}
                busy={generate.isPending || remove.isPending || share.isPending}
                selectionMode={selectionMode}
                selectedIds={selectedIds}
                onToggleRow={toggleRow}
                onToggleAll={toggleAllOnPage}
              />
            )}
          </div>
        </div>

        {/* Okno montujemy dopiero na czas otwarcia: zamknięcie ma je po prostu usunąć, a nie pokazywać
          przez chwilę stanu z wyłączonymi zapytaniami (0 modeli, ostrzeżenia). Ponowne otwarcie to
          świeży montaż, czyli reset filtrów bez dodatkowej logiki. */}
        {isDialogOpen && (
          <VisibilityReportDialog
            visible={isDialogOpen}
            onHide={() => {
              setIsDialogOpen(false);
              setFormError(null);
            }}
            onGenerate={handleGenerate}
            isBusy={generate.isPending}
            error={formError}
            projectId={projectId}
            brand={project?.projectLabel || project?.firmNameOrg || ''}
            minDate={pickerBounds.minDate}
            maxDate={pickerBounds.maxDate}
            defaultPeriodType={defaultPeriodType}
            features={project?.ownerFeatures ?? accountResponse.features ?? []}
          />
        )}

        <ConfirmDialog className="dialog-box" />
      </div>
    </EffectiveFeaturesProvider>
  );
};

export default VisibilityReportsPage;
